import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Star, Users, CheckCircle } from 'lucide-react';
import { Badge } from './ui/badge';
import { formatCurrency, getListingTypeLabel } from '../lib/utils';

const defaultImage = 'https://images.unsplash.com/photo-1563116128-261f46267cac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxydXN0aWMlMjBiYXJuJTIwaW50ZXJpb3IlMjBkYXJrfGVufDB8fHx8MTc2NzMyMTg4Mnww&ixlib=rb-4.1.0&q=85';

export const ListingCard = ({ listing, showStatus = false }) => {
  const primaryPhoto = listing.photos?.[0] || defaultImage;
  const location = listing.location || {};

  return (
    <Link
      to={`/listings/${listing.id}`}
      className="listing-card block group"
      data-testid={`listing-card-${listing.id}`}
    >
      <div className="listing-card-image relative">
        <img
          src={primaryPhoto}
          alt={listing.title}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = defaultImage; }}
        />
        <div className="absolute inset-0 gradient-overlay opacity-60" />
        
        {/* Type badge */}
        <div className="absolute top-3 left-3">
          <Badge className="bg-farmhouse-charcoal/80 text-farmhouse-cream border-0 backdrop-blur-sm">
            {getListingTypeLabel(listing.listing_type)}
          </Badge>
        </div>

        {/* Verified badge */}
        {listing.is_verified && (
          <div className="absolute top-3 right-3">
            <div className="flex items-center gap-1 bg-farmhouse-moss/90 text-white text-xs px-2 py-1 rounded-sm backdrop-blur-sm">
              <CheckCircle className="w-3 h-3" />
              <span>Verified</span>
            </div>
          </div>
        )}

        {/* Status badge for host view */}
        {showStatus && (
          <div className="absolute bottom-3 left-3">
            <Badge className={`capitalize ${
              listing.status === 'approved' ? 'bg-farmhouse-moss' :
              listing.status === 'submitted' ? 'bg-farmhouse-gold' :
              listing.status === 'rejected' ? 'bg-farmhouse-clay' :
              'bg-farmhouse-stone'
            } text-white border-0`}>
              {listing.status}
            </Badge>
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-serif text-lg font-semibold text-farmhouse-cream group-hover:text-farmhouse-saddle transition-colors line-clamp-1">
            {listing.title}
          </h3>
          {listing.average_rating > 0 && (
            <div className="flex items-center gap-1 text-farmhouse-gold shrink-0">
              <Star className="w-4 h-4 fill-current" />
              <span className="text-sm font-medium">{listing.average_rating.toFixed(1)}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 text-farmhouse-muted text-sm mb-3">
          <MapPin className="w-4 h-4" />
          <span className="line-clamp-1">
            {location.city}, {location.state}
          </span>
        </div>

        <div className="flex items-center gap-3 text-sm text-farmhouse-muted mb-3">
          <div className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            <span>{listing.capacity} {listing.capacity === 1 ? 'spot' : 'spots'}</span>
          </div>
          {listing.species_allowed?.length > 0 && (
            <span className="text-farmhouse-subtle">•</span>
          )}
          <span className="capitalize">{listing.species_allowed?.join(', ')}</span>
        </div>

        <div className="flex items-baseline gap-1">
          <span className="text-xl font-semibold text-farmhouse-cream">
            {formatCurrency(listing.pricing?.nightly || 0)}
          </span>
          <span className="text-farmhouse-muted text-sm">/ night</span>
        </div>
      </div>
    </Link>
  );
};

export default ListingCard;
